import { track } from '@plausible-analytics/tracker';

// ---------------------------------------------------------------------------
// Custom property helpers
// ---------------------------------------------------------------------------

/** Buckets a raw count into the coarse bands Plausible custom properties use. */
function countBucket(count: number): string {
  if (count <= 25) return '0–25';
  if (count <= 100) return '26–100';
  if (count <= 500) return '101–500';
  return '500+';
}

export const contactCountBucket = countBucket;
export const messageCountBucket = countBucket;

export type ImportMethod = 'csv' | 'google' | 'manual' | 'hubspot';
export type LimitType = 'contacts' | 'messages';
export type EditDistanceBucket = 'none' | 'minor' | 'major';

// ---------------------------------------------------------------------------
// Event names - single source of truth
// ---------------------------------------------------------------------------

export const AnalyticsEvent = {
  // Onboarding
  SignedUp: 'Signed Up',
  TutorialCompleted: 'Tutorial Completed',
  TutorialSkipped: 'Tutorial Skipped',
  ImportStarted: 'Import Started',
  ImportCompleted: 'Import Completed',
  FirstGrade: 'First Grade',
  GradingSessionCompleted: 'Grading Session Completed',
  FirstDraftViewed: 'First Draft Viewed',
  FirstMessageSent: 'First Message Sent',

  // Core loop
  MessageGenerated: 'Message Generated',
  DraftEdited: 'Draft Edited',
  MessageSent: 'Message Sent',
  RetrospectiveViewed: 'Retrospective Viewed',
  TemplateUpdated: 'Template Updated',

  // Monetisation
  FreeLimitHit: 'Free Limit Hit',
  PricingPageViewed: 'Pricing Page Viewed',
  UpgradeStarted: 'Upgrade Started',
  UpgradeCompleted: 'Upgrade Completed',

  // Engagement
  MobileGradeSession: 'Mobile Grade Session',
  DesktopSendSession: 'Desktop Send Session',
} as const;

// ---------------------------------------------------------------------------
// Typed track wrapper
// ---------------------------------------------------------------------------

type Props = Record<string, string>;

/**
 * Fire a Plausible custom event with optional props.
 *
 * This is a thin wrapper around `track` from `@plausible-analytics/tracker`.
 * It is a no-op in dev (when `init` was never called).
 */
export function trackEvent(eventName: string, props?: Props): void {
  try {
    track(eventName, { props });
  } catch {
    // Plausible not initialised (dev/test) - silently ignore
  }
}

// ---------------------------------------------------------------------------
// Edit distance bucket - compare AI draft to final message
// ---------------------------------------------------------------------------

export function editDistanceBucket(aiDraft: string, finalMessage: string): EditDistanceBucket {
  if (aiDraft === finalMessage) return 'none';

  const draftLen = aiDraft.length;
  if (draftLen === 0) return 'major';

  // Simple heuristic: Levenshtein is expensive; use length diff + char diff ratio
  const maxLen = Math.max(draftLen, finalMessage.length);
  let diff = 0;
  for (let i = 0; i < maxLen; i++) {
    if (aiDraft[i] !== finalMessage[i]) diff++;
  }
  const ratio = diff / maxLen;
  return ratio <= 0.2 ? 'minor' : 'major';
}

// ---------------------------------------------------------------------------
// Global custom properties - updated by AnalyticsPropsSync component
// ---------------------------------------------------------------------------

let _globalProps: Props = {};

/** Called by AnalyticsPropsSync to keep global dimensions current. */
export function setGlobalAnalyticsProps(props: Props): void {
  _globalProps = props;
}

/** Returns the current global props snapshot (for customProperties callback). */
export function getGlobalAnalyticsProps(): Props {
  return _globalProps;
}

// ---------------------------------------------------------------------------
// Device helpers
// ---------------------------------------------------------------------------

export function isMobileDevice(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}
